import React, { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { X, Save, Clock, Plus, Trash2, Loader } from 'lucide-react';
import { employesApi } from '../../utils/api';

// Registre des heures supplémentaires d'un salarié (historique daté + motif).
function RegistreHeuresSupp({ employeId }) {
  const [data, setData] = useState({ lignes: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ date: today, heures: '', motif: '' });

  const load = useCallback(() => {
    setLoading(true);
    employesApi.getHeuresSuppLog(employeId)
      .then(r => setData(r.data))
      .catch(() => setData({ lignes: [], total: 0 }))
      .finally(() => setLoading(false));
  }, [employeId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setErr('');
    const h = parseFloat(form.heures);
    if (!form.date || Number.isNaN(h) || h === 0) { setErr('Indiquez une date et un nombre d\'heures (≠ 0, négatif = récupéré).'); return; }
    setBusy(true);
    try { await employesApi.addHeuresSuppLog(employeId, { date: form.date, heures: h, motif: form.motif }); setForm({ date: today, heures: '', motif: '' }); load(); }
    catch (e) { setErr(e.response?.data?.error || 'Erreur'); }
    setBusy(false);
  };
  const remove = async (id) => {
    if (!window.confirm('Supprimer cette ligne du registre ?')) return;
    try { await employesApi.deleteHeuresSuppLog(id); load(); }
    catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };
  const fr = (d) => { try { return new Date(d + 'T00:00:00').toLocaleDateString('fr-FR'); } catch { return d; } };

  return (
    <section className="border border-gray-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-700 flex items-center gap-2 text-sm"><Clock size={15} className="text-navy-500" /> Registre des heures supplémentaires</h3>
        <span className={`text-sm font-bold ${data.total > 0 ? 'text-navy-700' : data.total < 0 ? 'text-red-500' : 'text-gray-300'}`}>Total : {Number(data.total || 0).toFixed(2)} h</span>
      </div>

      {/* Ajout d'une ligne */}
      <div className="grid grid-cols-12 gap-2 items-end mb-3">
        <div className="col-span-3">
          <label className="text-[11px] text-gray-500">Date</label>
          <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="form-input text-sm py-1.5" />
        </div>
        <div className="col-span-2">
          <label className="text-[11px] text-gray-500">Heures (+/−)</label>
          <input type="number" step="0.25" value={form.heures} onChange={e => setForm(f => ({ ...f, heures: e.target.value }))} placeholder="ex. 2 ou -1.5" className="form-input text-sm py-1.5" />
        </div>
        <div className="col-span-5">
          <label className="text-[11px] text-gray-500">Motif</label>
          <input type="text" value={form.motif} onChange={e => setForm(f => ({ ...f, motif: e.target.value }))} placeholder="ex. remplacement, réunion…" className="form-input text-sm py-1.5" />
        </div>
        <div className="col-span-2">
          <button type="button" onClick={add} disabled={busy} className="w-full inline-flex items-center justify-center gap-1 bg-navy-600 hover:bg-navy-700 text-white rounded-lg py-1.5 text-sm disabled:opacity-50">
            {busy ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />} Ajouter
          </button>
        </div>
      </div>
      {err && <p className="text-[12px] text-red-600 mb-2">{err}</p>}
      <p className="text-[11px] text-gray-400 mb-2">Heures effectuées = valeur positive ; heures récupérées / retirées = valeur négative. Le total alimente la fiche et le relevé mensuel.</p>

      {loading ? (
        <p className="text-sm text-gray-400 py-2">Chargement…</p>
      ) : data.lignes.length === 0 ? (
        <p className="text-[13px] text-gray-400">Aucune heure supplémentaire enregistrée.</p>
      ) : (
        <div className="border border-gray-100 rounded-lg divide-y divide-gray-100 max-h-56 overflow-auto">
          {data.lignes.map(l => (
            <div key={l.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="text-gray-500 w-24 flex-shrink-0">{fr(l.date)}</span>
              <span className={`font-semibold w-16 flex-shrink-0 ${l.heures >= 0 ? 'text-navy-700' : 'text-red-500'}`}>{l.heures > 0 ? '+' : ''}{Number(l.heures).toFixed(2)} h</span>
              <span className="flex-1 min-w-0 text-gray-600 truncate">{l.motif || <span className="text-gray-300 italic">—</span>}{l.saisi_par ? <span className="text-gray-300 text-[11px]"> · {l.saisi_par}</span> : null}</span>
              <button type="button" onClick={() => remove(l.id)} className="p-1.5 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 flex-shrink-0" title="Supprimer"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function EmployeForm({ employe, structures, onClose, onSaved }) {
  const isEdit = !!employe?.id;
  const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm();

  useEffect(() => {
    if (employe) {
      reset(employe);
    } else {
      reset({
        type_contrat: 'CDI',
        solde_cp: 0,
        solde_cp_anciennete: 0,
        heures_supp: 0,
        taux_horaire: 0,
        heures_hebdo_contrat: 35,
        taux_charges_patronales: 45,
        mutuelle_adhesion: 'base',
        mutuelle_part_patronale: 0,
        mutuelle_part_salariale: 0,
      });
    }
  }, [employe, reset]);

  const onSubmit = async (data) => {
    try {
      if (isEdit) {
        await employesApi.update(employe.id, data);
      } else {
        await employesApi.create(data);
      }
      onSaved();
    } catch (err) {
      alert('Erreur lors de la sauvegarde');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
          <h2 className="font-semibold text-gray-800">
            {isEdit ? 'Modifier la fiche employé' : 'Nouvel employé'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-6">

          {/* ── Identité ── */}
          <section>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-3 pb-1 border-b">Identité</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Prénom *</label>
                <input {...register('prenom', { required: true })} className="form-input" />
              </div>
              <div>
                <label className="form-label">Nom *</label>
                <input {...register('nom', { required: true })} className="form-input" />
              </div>
              <div>
                <label className="form-label">Poste</label>
                <input {...register('poste')} className="form-input" placeholder="Ex: Auxiliaire de puériculture" />
              </div>
              <div>
                <label className="form-label">Structure</label>
                <select {...register('structure_id')} className="form-select">
                  <option value="">—</option>
                  {structures.map(s => (
                    <option key={s.id} value={s.id}>{s.nom}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Email</label>
                <input type="email" {...register('email')} className="form-input" />
              </div>
              <div>
                <label className="form-label">Téléphone</label>
                <input {...register('telephone')} className="form-input" />
              </div>
              <div className="col-span-2">
                <label className="form-label">Adresse</label>
                <input {...register('adresse')} className="form-input" />
              </div>
              <div>
                <label className="form-label">N° Sécurité sociale</label>
                <input {...register('numero_secu')} className="form-input" placeholder="1 XX XX XX XXX XXX XX" />
              </div>
              <div>
                <label className="form-label">IBAN</label>
                <input {...register('iban')} className="form-input" placeholder="FR76 XXXX..." />
              </div>
            </div>
          </section>

          {/* ── Contrat ── */}
          <section>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-3 pb-1 border-b">Contrat de travail</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Type de contrat</label>
                <select {...register('type_contrat')} className="form-select">
                  <option value="CDI">CDI</option>
                  <option value="CDD">CDD</option>
                  <option value="Alternance">Alternance</option>
                  <option value="Stage">Stage</option>
                </select>
              </div>
              <div>
                <label className="form-label">Date d'embauche</label>
                <input type="date" {...register('date_embauche')} className="form-input" />
              </div>
              <div>
                <label className="form-label">Heures hebdomadaires contractuelles</label>
                <input type="number" step="0.5" {...register('heures_hebdo_contrat')} className="form-input" placeholder="35" />
              </div>
            </div>
          </section>

          {/* ── Rémunération & Charges ── */}
          <section>
            <div className="text-xs font-semibold text-primary-600 uppercase mb-3 pb-1 border-b border-primary-100">
              Rémunération & Charges
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Taux horaire brut (€/h)</label>
                <input type="number" step="0.01" {...register('taux_horaire')} className="form-input" placeholder="0.00" />
                <p className="text-[10px] text-gray-400 mt-0.5">Utilisé pour le calcul de refacturation</p>
              </div>
              <div>
                <label className="form-label">Taux de charges patronales (%)</label>
                <input type="number" step="0.1" {...register('taux_charges_patronales')} className="form-input" placeholder="45" />
                <p className="text-[10px] text-gray-400 mt-0.5">Par défaut 45% (secteur petite enfance)</p>
              </div>
            </div>

            {/* Aperçu coût employeur */}
          </section>

          {/* ── Mutuelle ── */}
          <section>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-3 pb-1 border-b">Mutuelle</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="form-label">Adhésion mutuelle</label>
                <select {...register('mutuelle_adhesion')} className="form-select">
                  <option value="aucune">Aucune / Dispense</option>
                  <option value="base">Base obligatoire</option>
                  <option value="option1">Option 1</option>
                  <option value="option2">Option 2</option>
                </select>
              </div>
              <div>
                <label className="form-label">Part patronale mensuelle (€)</label>
                <input type="number" step="0.01" {...register('mutuelle_part_patronale')} className="form-input" placeholder="0.00" />
                <p className="text-[10px] text-gray-400 mt-0.5">Prise en charge employeur / mois</p>
              </div>
              <div>
                <label className="form-label">Part salariale mensuelle (€)</label>
                <input type="number" step="0.01" {...register('mutuelle_part_salariale')} className="form-input" placeholder="0.00" />
                <p className="text-[10px] text-gray-400 mt-0.5">Retenu sur salaire</p>
              </div>
            </div>
          </section>

          {/* ── Congés & Soldes ── */}
          <section>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-3 pb-1 border-b">Congés & Heures</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">CP légaux (jours)</label>
                <input type="number" step="0.5" {...register('solde_cp')} className="form-input" />
              </div>
              <div>
                <label className="form-label">CP ancienneté (jours)</label>
                <input type="number" step="0.5" {...register('solde_cp_anciennete')} className="form-input" />
                <p className="text-[10px] text-gray-400 mt-0.5">+5j si ancienneté ≥ 2 ans</p>
              </div>
            </div>
          </section>

          {isEdit && <RegistreHeuresSupp employeId={employe.id} />}

          <div className="flex gap-3 pt-2 border-t">
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              <Save size={15} /> {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">Annuler</button>
          </div>
        </form>
      </div>
    </div>
  );
}
